import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Typography,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import { Delete, Edit, Visibility } from '@mui/icons-material';
import ProductDetail from './ProductDetails';
import ProductEdit from './ProductEdit';
import ProductRegister from './ProductRegister';
import api from '../services/api';
import { IProduto } from '../interfaces';

const ProductList: React.FC = () => {
    const [products, setProducts] = useState<IProduto[]>([]);
    const [selectedProduct, setSelectedProduct] = useState<IProduto | null>(null);
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [registerDialogOpen, setRegisterDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [productToDelete, setProductToDelete] = useState<IProduto | null>(null);

    // Carregar lista de produtos
    const fetchProducts = async () => {
        try {
            const response = await api.get('/api/products');
            setProducts(response.data);
        } catch (error) {
            console.error('Erro ao carregar produtos:', error);
        }
    };

    useEffect(() => {
        fetchProducts();
    }, []);

    // Abrir Dialog de Detalhamento
    const handleOpenDetailDialog = (product: IProduto) => {
        setSelectedProduct(product);
        setDetailDialogOpen(true);
    };

    // Abrir Dialog de Edição
    const handleOpenEditDialog = (product: IProduto) => {
        setSelectedProduct(product);
        setEditDialogOpen(true);
    };

    // Abrir Dialog de Registro
    const handleOpenRegisterDialog = () => {
        setRegisterDialogOpen(true);
    };

    // Abrir Dialog de Confirmação de Deleção
    const handleOpenDeleteDialog = (product: IProduto) => {
        setProductToDelete(product);
        setDeleteDialogOpen(true);
    };

    // Confirmar deleção
    const handleDeleteProduct = async () => {
        if (!productToDelete) return;

        try {
            await api.delete(`/api/products/${productToDelete._id}`);
            setDeleteDialogOpen(false);
            setProductToDelete(null);
            fetchProducts(); // Atualiza a lista de produtos
        } catch (error) {
            console.error('Erro ao deletar produto:', error);
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" sx={{ mb: 3 }}>
                Lista de Produtos
            </Typography>
            <Button variant="contained" color="primary" onClick={handleOpenRegisterDialog}>
                Registrar Novo Produto
            </Button>
            <Table sx={{ mt: 3 }}>
                <TableHead>
                    <TableRow>
                        <TableCell style={{ textAlign: 'center' }}>Nome</TableCell>
                        <TableCell style={{ textAlign: 'center' }}>Descrição</TableCell>
                        <TableCell style={{ textAlign: 'center' }}>Preço</TableCell>
                        <TableCell style={{ textAlign: 'center' }}>Ativo</TableCell>
                        <TableCell style={{ textAlign: 'center' }}>Faixas de Preço</TableCell>
                        <TableCell style={{ textAlign: 'center' }}>Ações</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {products.map((product) => (
                        <TableRow key={product._id}>
                            <TableCell>{product.name}</TableCell>
                            <TableCell>{product.description}</TableCell>
                            <TableCell style={{ textAlign: 'center' }}>
                                {product.preco !== undefined && product.preco !== null
                                    ? `R$ ${product.preco.toFixed(2)}`
                                    : 'N/A'}
                            </TableCell>
                            <TableCell style={{ textAlign: 'center' }}>{product.ativo ? 'Sim' : 'Não'}</TableCell>
                            <TableCell style={{ textAlign: 'center' }}>
                                {product.tiers && product.tiers.length > 0 ? (
                                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                        {product.tiers.map((tier, i) => (
                                            <li key={i}>{`${tier.rangeStart}-${tier.rangeEnd}: R$ ${tier.price.toFixed(2)}`}</li>
                                        ))}
                                    </ul>
                                ) : (
                                    'N/A'
                                )}
                            </TableCell>
                            <TableCell style={{ textAlign: 'center' }}>
                                <Tooltip title="Detalhes">
                                    <IconButton onClick={() => handleOpenDetailDialog(product)}>
                                        <Visibility />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Editar">
                                    <IconButton onClick={() => handleOpenEditDialog(product)}>
                                        <Edit />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Deletar">
                                    <IconButton onClick={() => handleOpenDeleteDialog(product)}>
                                        <Delete />
                                    </IconButton>
                                </Tooltip>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            {/* Dialog de Detalhamento */}
            <ProductDetail
                open={detailDialogOpen}
                onClose={() => setDetailDialogOpen(false)}
                product={selectedProduct}
            />

            {/* Dialog de Edição */}
            <ProductEdit
                open={editDialogOpen}
                onClose={() => setEditDialogOpen(false)}
                product={selectedProduct}
                onSave={fetchProducts}
            />

            {/* Dialog de Registro */}
            <ProductRegister
                open={registerDialogOpen}
                onClose={() => setRegisterDialogOpen(false)}
                onSave={fetchProducts}
            />

            {/* Dialog de Confirmação de Deleção */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Confirmar Deleção</DialogTitle>
                <DialogContent>
                    <Typography>
                        Tem certeza de que deseja deletar o produto{' '}
                        <strong>{productToDelete?.name}</strong>?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
                    <Button onClick={handleDeleteProduct} variant="contained" color="error">
                        Deletar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ProductList;